import type { LessonStep } from '@/types/lesson';

/** Результат валидации команды */
export interface ValidationResult {
  isValid: boolean;
  isExactMatch: boolean;
  matchedPattern?: string;
  feedback?: string;
}

/**
 * Нормализация команды для сравнения
 * Удаляет лишние пробелы и приводит к нижнему регистру
 */
export function normalizeCommand(command: string): string {
  return command.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Проверка соответствия команды ожидаемому шаблону
 */
export function validateCommand(
  inputCommand: string,
  expectedCommand: string,
  validationRegex?: string
): ValidationResult {
  const normalizedInput = normalizeCommand(inputCommand);
  const normalizedExpected = normalizeCommand(expectedCommand);

  // Точное совпадение
  if (normalizedInput === normalizedExpected) {
    return {
      isValid: true,
      isExactMatch: true,
      feedback: 'Отлично! Команда введена правильно.',
    };
  }

  // Проверка по regex если указан
  if (validationRegex) {
    try {
      const regex = new RegExp(validationRegex, 'i');
      if (regex.test(inputCommand)) {
        return {
          isValid: true,
          isExactMatch: false,
          matchedPattern: validationRegex,
          feedback: 'Команда принята. Есть небольшие отличия от ожидаемой.',
        };
      }
    } catch (e) {
      console.error('Invalid regex pattern:', validationRegex, e);
    }
  }

  // Проверка гибкого совпадения
  const flexibleResult = checkFlexibleMatch(normalizedInput, normalizedExpected);
  if (flexibleResult.isValid) {
    return flexibleResult;
  }

  // Команда не соответствует ожидаемой
  return {
    isValid: false,
    isExactMatch: false,
    feedback: generateFeedback(normalizedInput, normalizedExpected),
  };
}

/**
 * Гибкая проверка совпадения команды
 * Допускает вариации в порядке флагов и аргументов
 */
function checkFlexibleMatch(input: string, expected: string): ValidationResult {
  // Разбиваем на части
  const inputParts = input.split(/\s+/);
  const expectedParts = expected.split(/\s+/);

  // Проверяем, что базовая команда совпадает
  if (inputParts[0] !== expectedParts[0]) {
    return { isValid: false, isExactMatch: false };
  }

  // Для git команд проверяем подкоманду
  if (inputParts[0] === 'git') {
    if (inputParts[1] !== expectedParts[1]) {
      return { isValid: false, isExactMatch: false };
    }
  }

  // Извлекаем флаги и аргументы
  const inputFlags = extractFlags(inputParts);
  const expectedFlags = extractFlags(expectedParts);
  const inputArgs = extractArguments(inputParts);
  const expectedArgs = extractArguments(expectedParts);

  // Проверяем наличие всех обязательных флагов
  for (const flag of expectedFlags) {
    if (!inputFlags.includes(flag)) {
      return {
        isValid: false,
        isExactMatch: false,
        feedback: `Отсутствует флаг: ${flag}`,
      };
    }
  }

  // Проверяем аргументы
  if (inputArgs.length < expectedArgs.length) {
    return {
      isValid: false,
      isExactMatch: false,
      feedback: 'Недостаточно аргументов.',
    };
  }

  // Если все проверки пройдены
  return {
    isValid: true,
    isExactMatch: false,
    feedback: 'Команда принята.',
  };
}

/**
 * Извлечение флагов из команды
 */
function extractFlags(parts: string[]): string[] {
  return parts.filter((part) => part.startsWith('-'));
}

/**
 * Извлечение аргументов из команды
 */
function extractArguments(parts: string[]): string[] {
  return parts.filter((part) => !part.startsWith('-') && !isCommandKeyword(part));
}

/**
 * Проверка, является ли часть команды ключевым словом
 */
function isCommandKeyword(part: string): boolean {
  const keywords = [
    'git',
    'init',
    'status',
    'add',
    'commit',
    'log',
    'branch',
    'checkout',
    'switch',
    'merge',
    'reset',
    'revert',
    'rm',
    'mv',
    'remote',
    'fetch',
    'pull',
    'push',
    'stash',
    'config',
    'help',
  ];
  return keywords.includes(part);
}

/**
 * Генерация обратной связи для неправильной команды
 */
function generateFeedback(input: string, expected: string): string {
  const inputParts = input.split(/\s+/);
  const expectedParts = expected.split(/\s+/);

  // Проверяем базовую команду
  if (inputParts[0] !== expectedParts[0]) {
    return `Неожиданная команда. Ожидалось: ${expectedParts[0]}`;
  }

  // Для git команд проверяем подкоманду
  if (inputParts[0] === 'git' && inputParts[1] !== expectedParts[1]) {
    return `Неожиданная git подкоманда. Ожидалось: git ${expectedParts[1]}`;
  }

  // Проверяем наличие флагов
  const expectedFlags = extractFlags(expectedParts);
  const inputFlags = extractFlags(inputParts);

  const missingFlags = expectedFlags.filter((f) => !inputFlags.includes(f));
  if (missingFlags.length > 0) {
    return `Отсутствуют флаги: ${missingFlags.join(', ')}`;
  }

  // Проверяем количество аргументов
  const expectedArgs = extractArguments(expectedParts);
  const inputArgs = extractArguments(inputParts);

  if (inputArgs.length < expectedArgs.length) {
    return `Недостаточно аргументов. Ожидалось: ${expectedArgs.join(', ')}`;
  }

  return 'Команда не соответствует ожидаемой. Попробуйте использовать подсказку.';
}

/**
 * Извлечение имени команды из ввода
 */
export function extractCommandName(input: string): string {
  const parts = input.trim().split(/\s+/);

  if (parts[0] === 'git' && parts[1]) {
    return `git ${parts[1]}`;
  }

  return parts[0] ?? '';
}

/**
 * Проверка, является ли команда git командой
 */
export function isGitCommand(command: string): boolean {
  return command.trim().toLowerCase().startsWith('git ');
}

/**
 * Получение списка поддерживаемых git команд
 */
export function getSupportedGitCommands(): string[] {
  return [
    'git init',
    'git status',
    'git add',
    'git commit',
    'git log',
    'git branch',
    'git checkout',
    'git switch',
    'git merge',
    'git reset',
    'git revert',
    'git rm',
    'git mv',
    'git remote',
    'git fetch',
    'git pull',
    'git push',
    'git stash',
    'git config',
    'git help',
  ];
}

/**
 * Автозаполнение команды
 */
export function autocompleteCommand(input: string, expectedCommand?: string): string {
  const normalizedInput = input.toLowerCase();

  // Если есть ожидаемая команда, пытаемся автозаполнить её
  if (expectedCommand && expectedCommand.toLowerCase().startsWith(normalizedInput)) {
    return expectedCommand;
  }

  // Ищем среди поддерживаемых команд
  const supportedCommands = getSupportedGitCommands();
  const match = supportedCommands.find((cmd) => cmd.startsWith(normalizedInput));

  if (match) {
    return match;
  }

  return input;
}

/**
 * Парсинг аргументов команды
 */
export function parseCommandArguments(command: string): Record<string, string> {
  const args: Record<string, string> = {};
  const parts = command.trim().split(/\s+/);

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (part.startsWith('--')) {
      // Длинный флаг: --message "text"
      const flagName = part.slice(2);
      const nextPart = parts[i + 1];

      if (nextPart && !nextPart.startsWith('-')) {
        args[flagName] = nextPart;
        i++; // Пропускаем следующий элемент
      } else {
        args[flagName] = 'true';
      }
    } else if (part.startsWith('-') && !part.startsWith('--')) {
      // Короткий флаг: -m "text"
      const flagName = part.slice(1);
      const nextPart = parts[i + 1];

      if (nextPart && !nextPart.startsWith('-')) {
        args[flagName] = nextPart;
        i++;
      } else {
        args[flagName] = 'true';
      }
    }
  }

  return args;
}

/**
 * Валидация шага урока
 */
export function validateLessonStep(inputCommand: string, step: LessonStep): ValidationResult {
  const result = validateCommand(inputCommand, step.expectedCommand, step.validationRegex);

  return result;
}
